import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import useSiteSettings, { resolveSiteLogo } from "../../../hooks/useSiteSettings";
import { getTelegramContactLink } from "../../../utils/telegramContact";

const FOOTER_MENUS = [
  {
    key: "games",
    title: "게임",
    links: [
      { label: "카지노", to: "/casino-games?tab=casino" },
      { label: "슬롯", to: "/casino-games?tab=slots" },
      { label: "스포츠", to: "/sports-games" },
      { label: "룰렛", to: "/roulette" },
    ],
  },
  {
    key: "support",
    title: "고객지원",
    links: [
      { label: "이벤트", to: "/event" },
      { label: "공지사항", to: "/event?tab=anouncement" },
      { label: "고객문의", to: "/customer-service-center" },
      { label: "쪽지", to: "/notice-board" },
    ],
  },
  {
    key: "my",
    title: "마이메뉴",
    links: [
      { label: "마이페이지", to: "/my-page" },
      { label: "출석체크", to: "/attendance-check" },
    ],
  },
];

const FooterWrapper = styled.footer`
  width: 100%;
  border-top: 1px solid rgba(89, 244, 119, 0.1);
  background: #0b2028;

  .footer-container {
    width: min(1180px, 100%);
    margin: 0 auto;
    display: grid;
    grid-template-columns: minmax(190px, 0.75fr) minmax(0, 1.55fr);
    gap: 46px;
    padding: 38px 30px;
  }

  .footer-brand {
    min-height: 126px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid rgba(177, 205, 224, 0.1);
  }

  .logo {
    width: auto;
    max-width: 190px;
    max-height: 58px;
    object-fit: contain;
  }

  .footer-logo-skeleton {
    display: block;
    width: 168px;
    height: 42px;
    border-radius: 8px;
    background: linear-gradient(
      90deg,
      rgba(177, 205, 224, 0.07),
      rgba(177, 205, 224, 0.16),
      rgba(177, 205, 224, 0.07)
    );
    background-size: 220% 100%;
    animation: cryptoSkeletonShimmer 1.35s ease-in-out infinite;
  }

  .footer-menu-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 28px;
    align-content: center;
  }

  .footer-column {
    display: flex;
    flex-direction: column;
    gap: 11px;
    min-width: 0;
  }

  .footer-heading {
    margin: 0 0 2px;
    color: #f5fbff;
    font-size: 13px;
    line-height: 1.2;
    font-weight: 800;
  }

  .footer-link {
    width: fit-content;
    color: #9fb0bf;
    font-size: 12px;
    line-height: 1.2;
    font-weight: 600;
    text-decoration: none;
    transition:
      color 160ms ease,
      transform 160ms ease;
  }

  .footer-link:hover {
    color: var(--crypto-green);
    transform: translateX(2px);
  }

  .footer-telegram-link {
    margin-top: 3px;
    color: #59f477;
  }

  @keyframes cryptoSkeletonShimmer {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: -120% 0;
    }
  }

  @media (max-width: 760px) {
    .footer-container {
      grid-template-columns: 1fr;
      gap: 24px;
      padding: 32px 20px;
    }

    .footer-brand {
      min-height: 86px;
      border-right: 0;
      border-bottom: 1px solid rgba(177, 205, 224, 0.1);
      padding-bottom: 22px;
    }

    .logo {
      max-width: 170px;
      max-height: 52px;
    }

    .footer-menu-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 24px 20px;
    }
  }

  @media (max-width: 460px) {
    .footer-container {
      gap: 20px;
      padding: 28px 8px 82px;
    }

    .footer-menu-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 0;
      width: 100%;
    }

    .footer-column {
      align-items: center;
      gap: 10px;
      padding: 0 6px;
      text-align: center;
      border-right: 1px solid rgba(177, 205, 224, 0.08);
    }

    .footer-column:last-child {
      border-right: 0;
    }

    .footer-heading {
      font-size: 12px;
    }

    .footer-link {
      width: 100%;
      max-width: 96px;
      font-size: 11px;
      line-height: 1.25;
      word-break: keep-all;
    }

    .footer-link:hover {
      transform: none;
    }
  }
`;

const PageFooter = () => {
  const { settings, isLoading } = useSiteSettings();
  const backendLogo = resolveSiteLogo(settings);
  const telegramLink = getTelegramContactLink(settings);

  return (
    <FooterWrapper>
      <div className="footer-container">
        <div className="footer-brand">
          {backendLogo ? (
            <img src={backendLogo} alt="사이트 로고" className="logo" />
          ) : isLoading ? (
            <span className="footer-logo-skeleton" aria-hidden="true" />
          ) : null}
        </div>

        <nav className="footer-menu-grid" aria-label="푸터 메뉴">
          {FOOTER_MENUS.map((group) => (
            <div className="footer-column" key={group.key}>
              <p className="footer-heading">{group.title}</p>
              {group.links.map((link) => (
                <Link className="footer-link" to={link.to} key={`${group.title}-${link.label}`}>
                  {link.label}
                </Link>
              ))}
              {group.key === "support" && telegramLink ? (
                <a
                  className="footer-link footer-telegram-link"
                  href={telegramLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  텔레그램 문의
                </a>
              ) : null}
            </div>
          ))}
        </nav>
      </div>
    </FooterWrapper>
  );
};

export default PageFooter;
